import React, { useState, useEffect, useRef, ReactElement, Fragment, useCallback } from 'react';
import styled, { CSSObject } from '@emotion/styled';
import { getPatternModal } from '../../../style';
import { connect, useDispatch, useSelector } from 'react-redux';
import Avatar from '../../../atoms/Avatar';
import Headline from '../../common/Headline';
import TextLink from '../../common/TextLink';
import PriceInput, { adjustStylePerDigit } from './PriceInput';
import Button from '../../../atoms/buttons/Button';
import ButtonSet from '../../blocks/ButtonSet';
import {
  sendOffer,
  sendCancel,
  counterOffer,
  counterCancel,
  counterModalCustomAmount,
  acceptOffer,
  counterModalClose,
} from '../../../actions/profileTrackAction';
import TransactionAwareness from './TransactionAwareness';
import useModal from '../../../hooks/useModal';
import WYPButtonIcon from '../../../atoms/icons/WYPButtonIcon';
import useOnClickTriggerTracking from '../../../hooks/useOnclickTriggerTracking';
import { PriceContainer, getInputProps, CurrencyTip, CurrencyLabelSpan } from './common';
import { Account, AccountMaker } from '../../../models/Account';
import useMixPanel from '../../../hooks/useMixPanel';
import { EVENT_NAMES } from '../../../constants/mixpanel';
import { clearErrors } from '../../../actions/commonActions';
import { RootState } from '../../../store';
import useMandatoryWall from '../../../hooks/useMandatoryWall';
import { OfferActions } from 'utils/offers';
import { useCommonModal } from 'contexts/ModalContext';
import ModalWithPattern from 'components/common/modals/ModalWithPattern';
import { IsMobileViewPort } from 'utils/helpers';
import { mobileContentStyle, mobileOverlayStyle } from 'components/common/CommonStyle';
import OfferModalFooter from './OfferModalFooter';
import breakpoints from 'theme/material/breakpoints';
import { isMobile } from 'react-device-detect';
import borders from 'theme/borders';

const Form = styled('form')({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
});

const HeaderDiv = styled('div')({
  textAlign: 'center',

  [breakpoints.down(639.05)]: {
    marginTop: 10,
  },
});

const AvatarTitleDiv = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  flexWrap: 'wrap',
  justifyContent: 'center',
  alignItems: 'center',
  margin: '0 auto',
  gap: 20,
});

const TitleDiv = styled('span')(
  (): CSSObject => ({
    color: getPatternModal().titleColor,
    fontSize: 36,
    fontWeight: 800,
    lineHeight: '40px',
    letterSpacing: -0.3,
    textTransform: 'uppercase',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    [breakpoints.down(639.05)]: {
      fontSize: 30,
    },
  })
);

const Content = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  justifyContent: 'space-between',
});

const colorType = (price: boolean, hasErrors: boolean) => {
  switch (price) {
    case true:
      return 'default';
    case false:
      return !hasErrors ? 'brand' : 'alert';
    default:
      return;
  }
};

const CounterRange = styled('div')({
  fontSize: 14,
  lineHeight: '19px',
  letterSpacing: '-0.3px',
  marginTop: 20,
  marginBottom: 20,
  textAlign: 'center',
  color: getPatternModal().headerColor,
});

const ErrorContainer = styled('div')({
  textAlign: 'center',
  marginTop: '20px',
});

const ErrorMessage = styled('div')({
  color: 'rgb(204, 84, 84)',
  margin: '16px auto 0',
  maxWidth: '280px',
});

const PurchaseCredits = styled('div')({
  textAlign: 'center',
  marginTop: '12px',
  textDecoration: 'underline',
});

const ButtonDiv = styled('div')({
  marginTop: isMobile ? 0 : 20,
});

const getHeadline = (isCounter: boolean, username: string | null) => {
  if (isCounter) {
    return (
      <span>
        Counter <strong>{username}&lsquo;s</strong> offer by submitting a new amount below.
      </span>
    );
  } else {
    return (
      <span>
        Accept <strong>{username}&lsquo;s</strong> offer or submit a{' '}
        <strong>
          <i> Counter Offer </i>
        </strong>
        and negotiate a better first date offer.
      </span>
    );
  }
};

const offerMinMaxVal = (offer, type, defaultAmount) => {
  const offerVal = offer && type === 'min' ? offer.min_counter_price : offer && offer.max_counter_price;

  return offer ? offerVal : defaultAmount;
};

const validateTextInput = e => {
  const Key = e.key;
  const regex = /[0-9]/;

  if (!regex.test(Key)) {
    if (e.preventDefault) e.preventDefault();
  }
};

const renderCounterRange = (
  isCounter: boolean,
  currencyInitial: number,
  currLabel: string,
  minValue: number,
  maxValue: number
) => {
  if (isCounter) {
    return (
      <CounterRange>
        Enter a new amount between {currencyInitial}
        {currLabel}
        {minValue} and {currencyInitial}
        {currLabel}
        {maxValue}
      </CounterRange>
    );
  }
  return null;
};

const setCreateOfferAction = (_e: Event, offerAction: string) => {
  switch (offerAction) {
    case 'Send Counter':
      counterOffer();
      break;
    case 'Cancel Counter':
      counterCancel();
      break;
    case 'Send Offer':
      sendOffer();
      break;
    case 'Accept Offer':
      acceptOffer();
      break;
    case 'Cancel Offer':
      sendCancel();
      break;
    case 'Counter Modal Close':
      counterModalClose();
      break;
    default:
      return;
  }
};

const trackFocus = (ref, actionMethod) => {
  const handler = e => actionMethod(e);

  if (ref && ref.current) {
    ref.current.addEventListener('click', handler, false);
  }

  return () => {
    ref.current.removeEventListener('click', handler, false);
  };
};

interface OfferAcceptProps {
  isModalOpen: boolean;
  avatarSrc: any;
  offer: any;
  closeModal: any;
  currentUserIsGenerous: any;
  username: any;
  counterOffer: any;
  onHandleSubmit: any;
  onHandleAttractiveSubmit: any;
  hashId: any;
  buttonID: any;
  acceptUnlock: any;
  ignoreOffer: any;
  successOpen: any;
  forceCounter: boolean;
  pageTitle: string;
  accountModel: Account;
  customContentStyle?: any;
  forceTrigger?: any;
}

const OfferAccept = ({
  isModalOpen,
  avatarSrc,
  offer,
  closeModal,
  currentUserIsGenerous,
  username,
  counterOffer,
  onHandleSubmit,
  onHandleAttractiveSubmit,
  hashId,
  buttonID,
  acceptUnlock,
  ignoreOffer,
  successOpen,
  forceCounter = false,
  pageTitle,
  accountModel,
  customContentStyle,
  forceTrigger,
}: OfferAcceptProps): ReactElement => {
  const dispatch = useDispatch();
  const errors = useSelector((state: RootState) => state.common.errors);

  const { mixpanelData } = useMixPanel();
  const [defaultValue] = useState('');
  const [offerPrice, setOfferPrice] = useState(offer?.data?.price_original);
  const [minValue, setMinValue] = useState(offerMinMaxVal(offer.data, 'min', '5'));
  const [maxValue, setMaxValue] = useState(offerMinMaxVal(offer.data, 'max', '500'));
  const [hasErrors, setHasErrors] = useState(false);
  const [isOfferSending, setOfferSending] = useState(false);
  const [isCounter, setIsCounter] = useState(forceCounter);
  const sendOfferBtn = useRef(null);
  const declineOfferBtn = useRef(null);
  const sendCancelBtn = useRef(null);
  const priceInput = useRef(null);
  const [currencyInitial, setCurrencyInitial] = useState('');
  const [currencyLabel, setCurrencyLabel] = useState('');
  const { showMandatoryWall } = useMandatoryWall();

  const { resetModal, resetModalPosition, setModalStateHandler, confirmDeclineOffer } = useModal();
  const { setShowTooManyPendingOfferModal, setModalArgs } = useCommonModal();

  useOnClickTriggerTracking('Cancel Offer', sendCancelBtn, setCreateOfferAction);

  useOnClickTriggerTracking(forceCounter ? 'Counter Modal Close' : '', sendCancelBtn, setCreateOfferAction);

  const handleClearErrors = useCallback(() => {
    dispatch(clearErrors());
  }, []);

  useEffect(() => {
    // vysion events trigger when closing the counter offer modal
    if (isCounter) {
      setModalStateHandler({
        onCloseClick: () => {
          counterModalClose();
          resetModal();
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCounter]);

  useEffect(() => {
    handleClearErrors();
    trackFocus(priceInput, counterModalCustomAmount);
  }, []);

  useEffect(() => {
    const { data } = offer;

    setMaxValue(offerMinMaxVal(data, 'max', '500'));
    setMinValue(offerMinMaxVal(data, 'min', '5'));
    setCurrencyInitial(data.currency_original.data.initial);
    setCurrencyLabel(data.currency_original.data.label_unicode);

    if (forceTrigger && forceCounter) {
      setOfferPrice('');
      priceInput.current?.focus();
      return;
    }

    setOfferPrice(data.price_original);
  }, [offer, forceTrigger, forceCounter]);

  useEffect(() => {
    setIsCounter(forceCounter || offerPrice?.toString() !== offer?.data?.price_original?.toString());
  }, [offerPrice, forceCounter, offer.data.price_original]);

  const updateOfferPrice = (val, error) => {
    if (!error) {
      // dispatch(clearErrors());
    }
    setOfferPrice(val);
    setHasErrors(error || false);
  };

  const inputOnChange = e => {
    if (!e.target || typeof e.target.value !== 'string') {
      return;
    }

    const { data } = offer;
    const inputVal = Number(e.target.value);
    const minValueNum = Number(minValue);
    const maxValueNum = Number(maxValue);
    const maxLength = maxValueNum.toString().length;

    if (e.target.value.length <= maxLength) {
      let isError = inputVal < minValueNum || inputVal > maxValueNum;

      if (data?.price_original === inputVal) {
        isError = false;
      }

      updateOfferPrice(inputVal, isError);
    } else {
      e.preventDefault();
    }
  };

  const mixpanelCounterOfferMadeSource = (pageTitle: string) => {
    const mxOfferMadePayload = mixpanelData[EVENT_NAMES.OFFER_MADE];

    const source = {
      Profile: mxOfferMadePayload.source,
    };

    return pageTitle in source ? source[pageTitle] : pageTitle;
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    let offerAction = 'accept' as OfferActions;

    if (isCounter) {
      offerAction = 'counter';
    } else if (offer && offer.suggest) {
      offerAction = 'suggest';
    }

    const counterOfferFormData = {
      price: offerPrice,
      account: hashId,
      source: mixpanelCounterOfferMadeSource(pageTitle),
    };
    const offerAmount = `${currencyInitial}${currLabel}${offerPrice}`;

    setOfferSending(true);
    showMandatoryWall(
      () => {
        if (accountModel.shouldShowTooManyLockedOffersDeals(offerAction)) {
          closeModal();
          setShowTooManyPendingOfferModal(true);
          return;
        }

        if (isCounter && accountModel.shouldShowTooManySentOffers()) {
          closeModal();
          setShowTooManyPendingOfferModal(true);
          setModalArgs({ type: 'sent' });
          return;
        }

        if (isCounter || (offer && offer.suggest)) {
          counterOffer(counterOfferFormData)
            .then(() => {
              closeModal();
              setOfferSending(false);
              successOpen(true, offerAmount, true);
            })
            .catch(() => {
              setOfferSending(false);
              setHasErrors(true);
            });

          // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
          setCreateOfferAction(e, 'Send Counter');
        } else {
          currentUserIsGenerous
            ? onHandleSubmit(offerAmount)
                .then(res => {
                  setOfferSending(false);
                  closeModal();
                  acceptUnlock(offerAmount, res.credit_cost, res.threadUid, res.to_account_hash_id, username, false);
                  // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
                  setCreateOfferAction(e, 'Accept Offer');
                })
                .catch(() => {
                  setOfferSending(false);
                  setHasErrors(true);
                })
            : onHandleAttractiveSubmit(offerAmount);
          setOfferSending(false);
        }
      },
      () => setOfferSending(false),
      isCounter || (offer && offer.suggest) ? 'Counter Offer' : 'Accept Offer'
    );
  };

  // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
  const currLabel = String.fromCharCode(currencyLabel);

  const submitBtnLabel = () => {
    if (isCounter) {
      return 'Send Counter Offer';
    }
    const initialLabel = 'Accept';

    return isOfferSending ? 'Accepting Offer' : initialLabel;
  };

  useEffect(() => {
    if (isCounter) {
      resetModalPosition();
    }
  }, [isCounter, resetModalPosition]);

  return (
    <ModalWithPattern
      isModalOpen={isModalOpen}
      dataTestID="offer-accept"
      closeBtnClassName="offer-accept-modal-close"
      closeBtnDataTestId="offer-accept-modal-close"
      modalContentLabel="Offer Accept Modal"
      onCloseClick={closeModal}
      closeRequest={closeModal}
      customOverlayStyle={IsMobileViewPort ? mobileOverlayStyle : {}}
      customContentStyle={IsMobileViewPort ? { ...mobileContentStyle, ...customContentStyle } : customContentStyle}
      withoutBgPattern={false}
      hideCloseBtn={false}
      closeOnOverlayClick={true}
      enableTouchMove={true}
    >
      <Fragment>
        <Form>
          <HeaderDiv>
            <AvatarTitleDiv>
              <Avatar
                data-test-id="offer-modal-avatar"
                src={avatarSrc}
                width={'100px'}
                customStyle={{
                  backgroundColor: '#ccc',
                  border: `1px solid ${borders.light.primary}`,
                }}
              />
              <TitleDiv>{isCounter ? 'Counter Offer?' : 'Accept Offer?'}</TitleDiv>
            </AvatarTitleDiv>
            <Headline
              style={{
                color: getPatternModal().headerColor,
                marginTop: 20,
                fontSize: 16,
                lineHeight: '21px',
                letterSpacing: '-0.3px',
              }}
              data-test-id="profile-offer-headline"
              size="SM"
            >
              {getHeadline(isCounter, username)}
            </Headline>
          </HeaderDiv>
          <Content>
            <div>
              <PriceContainer
                // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
                hasErrors={hasErrors}
              >
                <CurrencyTip
                  offerPrice={colorType(!!offerPrice, hasErrors)}
                  style={{
                    paddingLeft: adjustStylePerDigit(offerPrice).paddingLeft,
                  }}
                >
                  <CurrencyLabelSpan
                    customCurrencyLabelStyle={{
                      fontSize: 60,
                      fontWeight: 400,
                      lineHeight: '50px',
                      letterSpacing: -0.3,
                      [breakpoints.down('sm')]: { fontSize: '50px', lineHeight: '50px' },
                    }}
                  >
                    {currencyInitial}
                    {currLabel}
                  </CurrencyLabelSpan>
                  <PriceInput
                    inputRef={priceInput}
                    data-test-id="offer-price-input"
                    hasErrors={hasErrors}
                    value={offerPrice}
                    onChange={inputOnChange}
                    validateTextFn={validateTextInput}
                    placeholder={defaultValue}
                    inputProps={{
                      style: {
                        ...getInputProps().style,
                        marginLeft: adjustStylePerDigit(offerPrice).marginLeft,
                        textAlign: 'left',
                        fontSize: 60,
                        fontWeight: 400,
                        lineHeight: '50px',
                        letterSpacing: -0.3,
                      },
                    }}
                    minWidth={IsMobileViewPort ? 100 : 120}
                    responsiveFontSize={`
                      @media (max-width: 1023.95px) {
                        font-size: 50px !important;
                        line-height: 50px !important;
                      }
                   `}
                  />
                </CurrencyTip>
              </PriceContainer>
              {isCounter ? (
                // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
                renderCounterRange(isCounter, currencyInitial, currLabel, minValue, maxValue)
              ) : (
                <CounterRange data-test-id="counter-offer-desc">
                  To submit a <strong style={{ fontWeight: 600 }}>Counter Offer</strong>, enter a new amount before
                  submitting.
                </CounterRange>
              )}

              {errors && Object.keys(errors).length ? (
                <ErrorContainer>
                  <ErrorMessage>{errors.price}</ErrorMessage>
                  {currentUserIsGenerous && offerPrice && Number(offerPrice) >= 200 && (
                    <PurchaseCredits>
                      <TextLink to="/packages" onClick={() => closeModal()}>
                        Purchase credits now.
                      </TextLink>
                    </PurchaseCredits>
                  )}
                </ErrorContainer>
              ) : null}
            </div>
            <ButtonDiv>
              <ButtonSet align="center">
                {!forceCounter && !isCounter && (
                  <Button
                    id={buttonID}
                    buttonRef={declineOfferBtn}
                    disabled={hasErrors || isOfferSending}
                    buttonType="secondaryBlue"
                    onClick={e => {
                      e.preventDefault();
                      closeModal();

                      if (accountModel.shouldShowTooManyLockedOffersDeals('decline')) {
                        setShowTooManyPendingOfferModal(true);
                      } else {
                        confirmDeclineOffer({
                          isGenerous: currentUserIsGenerous,
                          username: username,
                          onConfirm: () => {
                            ignoreOffer();
                          },
                        });
                      }
                    }}
                    type="submit"
                    customStyle={{
                      fontSize: 18,
                      lineHeight: '16px',
                      letterSpacing: -0.3,
                      padding: '12px 28px',
                      borderRadius: '4px !important',
                      maxWidth: 135,
                    }}
                    data-test-id="modal-decline-offer-btn"
                  >
                    Decline
                  </Button>
                )}

                <Button
                  id={buttonID}
                  buttonRef={sendOfferBtn}
                  disabled={hasErrors || isOfferSending}
                  buttonType="error"
                  onClick={handleSubmit}
                  type="submit"
                  data-test-id="modal-accept-offer-btn"
                  customStyle={{
                    fontSize: 18,
                    lineHeight: '16px',
                    letterSpacing: -0.3,
                    padding: '12px 28px',
                    borderRadius: '4px !important',
                    maxWidth: !forceCounter && !isCounter ? 135 : '100%',
                  }}
                >
                  <WYPButtonIcon style={{ marginRight: 6 }} />
                  {submitBtnLabel()}
                </Button>
              </ButtonSet>
              <ButtonSet align="center">
                <Button
                  buttonType="chromeless"
                  buttonRef={sendCancelBtn}
                  data-test-id={`${buttonID}Cancel`}
                  disabled={isOfferSending}
                  onClick={e => {
                    e.preventDefault();
                    closeModal();
                    setOfferSending(false);
                  }}
                  customStyle={{
                    color: getPatternModal().linkGrey,
                    fontSize: 15,
                    lineHeight: '17px',
                    fontWeight: 'normal',
                    height: 'auto',
                    padding: '20px 0',
                  }}
                >
                  Cancel
                </Button>
              </ButtonSet>
            </ButtonDiv>
          </Content>
          {!isCounter && !isMobile && <TransactionAwareness />}
        </Form>
      </Fragment>
      <OfferModalFooter />
    </ModalWithPattern>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    accountModel: AccountMaker.create(state.profile),
  };
};

export default React.memo(connect(mapStateToProps, {})(OfferAccept));
